import { useState } from "react";
import { Mail, Clock, Send } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Reveal, SectionHeading } from "@/components/motion/Reveal";
import api, { apiErrorMessage } from "@/lib/api";
import { useFormFields, isEmail } from "@/hooks/useFormFields";

const SEDE_OPTIONS = ["Napoli - Viale Augusto", "Napoli - P.tta Falcone", "Milano - Corso Porta Romana"];
const AREA_OPTIONS = ["Diritto Civile e Contratti", "Infortunistica e Risarcimento", "Diritto Tributario", "Recupero Crediti", "Altro"];

const EMPTY = { nome: "", email: "", telefono: "", sede: "", area: "", messaggio: "", gdpr: false };

const CONTACT_ITEMS = [
  { icon: Mail, label: "Email", value: "avv.giuseppenapolitano@gmail.com", href: "mailto:avv.giuseppenapolitano@gmail.com", testid: "contact-email" },
  { icon: Clock, label: "Ricevimento", value: "Solo su appuntamento", testid: "contact-hours" },
];

const VALIDATORS = {
  nome: (v) => v.trim().length < 2 && "Inserisci nome e cognome",
  email: (v) => !isEmail(v) && "Inserisci un indirizzo email valido",
  telefono: (v) => v.trim().length < 6 && "Inserisci un numero di telefono valido",
  sede: (v) => !v && "Seleziona la sede di riferimento",
  area: (v) => !v && "Seleziona l'area di interesse",
  messaggio: (v) => v.trim().length < 10 && "Descrivi brevemente il tuo caso (min. 10 caratteri)",
  gdpr: (v) => !v && "Il consenso al trattamento dei dati è obbligatorio",
};

const Contact = () => {
  const { form, errors, set, validate, reset } = useFormFields(EMPTY, VALIDATORS);
  const [loading, setLoading] = useState(false);

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await api.post("/consultations", form);
      toast.success("Richiesta inviata con successo", {
        description: "Verrai ricontattato dallo Studio entro 24 ore lavorative.",
      });
      reset();
    } catch (err) {
      toast.error("Invio non riuscito", { description: apiErrorMessage(err) });
    } finally {
      setLoading(false);
    }
  };

  const fieldError = (key) => errors[key] && <p className="mt-1 text-xs text-red-600" data-testid={`error-${key}`}>{errors[key]}</p>;

  return (
    <section id="contatti" className="bg-ivory py-24 sm:py-32" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <SectionHeading
          index="04"
          eyebrow="Contatti & Appuntamenti"
          title="Mettiti in Contatto con lo Studio"
          subtitle="Compila il modulo sottostante per richiedere una consulenza o concordare un appuntamento in studio. Verrai ricontattato direttamente."
        />

        <div className="mt-16 grid gap-12 lg:grid-cols-5">
          <div className="flex flex-col gap-5 lg:col-span-2">
            {CONTACT_ITEMS.map((c, i) => (
              <Reveal key={c.label} delay={i * 0.1}>
                <div className="flex items-start gap-5 border border-navy/10 bg-white p-6" data-testid={c.testid}>
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center border border-gold/40 bg-gold/10 text-gold-dark">
                    <c.icon className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-slate-500">{c.label}</p>
                    {c.href ? (
                      <a href={c.href} className="mt-1 block font-medium text-navy hover:text-gold-dark transition-colors break-all">
                        {c.value}
                      </a>
                    ) : (
                      <p className="mt-1 font-medium text-navy">{c.value}</p>
                    )}
                  </div>
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.3}>
              <div className="bg-navy p-6">
                <p className="font-display text-lg italic text-white">«Ogni richiesta merita ascolto.»</p>
                <p className="mt-2 text-sm text-slate-300">
                  Le richieste inviate tramite il modulo vengono esaminate personalmente: verrai ricontattato allo recapiti indicati.
                </p>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.15} className="lg:col-span-3">
            <form onSubmit={submit} className="border border-navy/10 bg-white p-7 sm:p-10 shadow-sm" data-testid="consultation-form" noValidate>
              <div className="grid gap-6 sm:grid-cols-2">
                <div>
                  <Label htmlFor="nome">Nome e Cognome *</Label>
                  <Input id="nome" data-testid="input-nome" className="mt-2" value={form.nome} onChange={(e) => set("nome", e.target.value)} placeholder="Mario Rossi" />
                  {fieldError("nome")}
                </div>
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input id="email" type="email" data-testid="input-email" className="mt-2" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="mario.rossi@email.it" />
                  {fieldError("email")}
                </div>
                <div>
                  <Label htmlFor="telefono">Telefono *</Label>
                  <Input id="telefono" type="tel" data-testid="input-telefono" className="mt-2" value={form.telefono} onChange={(e) => set("telefono", e.target.value)} placeholder="+39 333 000 0000" />
                  {fieldError("telefono")}
                </div>
                <div>
                  <Label>Sede di Riferimento *</Label>
                  <Select value={form.sede} onValueChange={(v) => set("sede", v)}>
                    <SelectTrigger className="mt-2" data-testid="select-sede">
                      <SelectValue placeholder="Seleziona la sede" />
                    </SelectTrigger>
                    <SelectContent>
                      {SEDE_OPTIONS.map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {fieldError("sede")}
                </div>
                <div className="sm:col-span-2">
                  <Label>Area di Interesse *</Label>
                  <Select value={form.area} onValueChange={(v) => set("area", v)}>
                    <SelectTrigger className="mt-2" data-testid="select-area">
                      <SelectValue placeholder="Seleziona l'area di interesse" />
                    </SelectTrigger>
                    <SelectContent>
                      {AREA_OPTIONS.map((a) => (
                        <SelectItem key={a} value={a}>{a}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {fieldError("area")}
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="messaggio">Messaggio *</Label>
                  <Textarea
                    id="messaggio"
                    data-testid="textarea-messaggio"
                    className="mt-2 min-h-[130px]"
                    value={form.messaggio}
                    onChange={(e) => set("messaggio", e.target.value)}
                    placeholder="Descrivi sinteticamente il tuo caso o la tua richiesta..."
                  />
                  {fieldError("messaggio")}
                </div>
                <div className="sm:col-span-2">
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="gdpr"
                      data-testid="checkbox-gdpr"
                      checked={form.gdpr}
                      onCheckedChange={(v) => set("gdpr", v === true)}
                      className="mt-0.5"
                    />
                    <Label htmlFor="gdpr" className="text-sm font-normal leading-relaxed text-slate-600 cursor-pointer">
                      Acconsento al trattamento dei miei dati personali ai sensi del Regolamento UE 2016/679 (GDPR), per essere ricontattato dallo Studio. *
                    </Label>
                  </div>
                  {fieldError("gdpr")}
                </div>
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="submit-consultation-button"
                className="group mt-8 inline-flex w-full sm:w-auto items-center justify-center gap-3 bg-gold px-10 py-4 text-sm font-semibold uppercase tracking-wider text-navy transition-colors hover:bg-gold-light disabled:opacity-60"
              >
                {loading ? "Invio in corso..." : "Invia Richiesta"}
                <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Contact;
